import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isAiNotesEnabled, isAiNotesPanelVisible } from '../../../ai-notes/functions';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { getConferenceTimestamp } from '../../../base/conference/functions';
import Icon from '../../../base/icons/components/Icon';
import { IconShare, IconShareDoc } from '../../../base/icons/svg';
import { getLocalParticipant } from '../../../base/participants/functions';
import statsEmitter from '../../../connection-indicator/statsEmitter';
import { beginAddPeople } from '../../../invite/actions.any';
import { toggleSidePanelApp } from '../../../side-panel/actions';
import { SidePanelTabId } from '../../../side-panel/constants';
import logger from '../../logger';
/** 顶栏左侧图标尺寸。 */
const HEADER_LEFT_ICON_SIZE_PX = 14;
/** 时长刷新间隔。 */
const HEADER_DURATION_TICK_MS = 1000;
/** 信号条数量。 */
const SIGNAL_BAR_COUNT = 3;
/** 信号满格阈值。 */
const SIGNAL_QUALITY_HIGH = 70;
/** 信号中等阈值。 */
const SIGNAL_QUALITY_MEDIUM = 35;
const SIGNAL_COLOR_GOOD = '#22c55e';
const SIGNAL_COLOR_MEDIUM = '#eab308';
const SIGNAL_COLOR_POOR = '#ef4444';
const SIGNAL_COLOR_IDLE = '#c0c4cc';
const useStyles = makeStyles()(theme => {
    return {
        root: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            height: '100%',
            flexShrink: 0,
            minWidth: 0
        },
        timer: {
            ...theme.typography.labelRegular,
            color: theme.palette.conferenceSubjectText || theme.palette.text02,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
        },
        signal: {
            display: 'inline-flex',
            alignItems: 'flex-end',
            gap: '2px',
            height: '14px',
            padding: '0 4px',
            border: 'none',
            background: 'transparent',
            cursor: 'default'
        },
        signalBar: {
            display: 'block',
            width: '3px',
            borderRadius: '1px'
        },
        iconButton: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            padding: 0,
            border: 'none',
            borderRadius: '4px',
            background: 'transparent',
            color: theme.palette.conferenceSubjectText || theme.palette.text02,
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.ui03
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '1px'
            }
        },
        iconButtonActive: {
            backgroundColor: theme.palette.action02,
            color: theme.palette.action01
        },
        divider: {
            width: '1px',
            height: '16px',
            margin: '0 2px',
            backgroundColor: theme.palette.ui04,
            flexShrink: 0
        }
    };
});
/**
 * 格式化会议时长为 HH:MM:SS。
 *
 * @param {number} elapsedMs - 已过毫秒。
 * @returns {string}
 */
function formatHeaderDuration(elapsedMs) {
    const safe = Math.max(0, Math.floor(elapsedMs / 1000));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
/**
 * 根据连接质量得到点亮条数与颜色。
 *
 * @param {number} quality - 0–100。
 * @param {boolean} hasStats - 是否已入会并有统计。
 * @returns {{ level: number; color: string }}
 */
function resolveSignalDisplay(quality, hasStats) {
    if (!hasStats) {
        return { color: SIGNAL_COLOR_IDLE, level: 0 };
    }
    if (quality >= SIGNAL_QUALITY_HIGH) {
        return { color: SIGNAL_COLOR_GOOD, level: SIGNAL_BAR_COUNT };
    }
    if (quality >= SIGNAL_QUALITY_MEDIUM) {
        return { color: SIGNAL_COLOR_MEDIUM, level: 2 };
    }
    return { color: SIGNAL_COLOR_POOR, level: 1 };
}
/**
 * 会议顶栏左侧：时长、网络信号、分享会议、AI 纪要。
 *
 * @returns {JSX.Element}
 */
const ConferenceHeaderLeft = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const startTimestamp = useSelector(getConferenceTimestamp);
    const localParticipantId = useSelector((state) => getLocalParticipant(state)?.id);
    const aiNotesEnabled = useSelector(isAiNotesEnabled);
    const aiNotesOpen = useSelector(isAiNotesPanelVisible);
    const [durationText, setDurationText] = useState(formatHeaderDuration(0));
    const [connectionQuality, setConnectionQuality] = useState(100);
    const [hasConnectionStats, setHasConnectionStats] = useState(false);
    useEffect(() => {
        if (!startTimestamp) {
            setDurationText(formatHeaderDuration(0));
            return;
        }
        const tick = () => {
            setDurationText(formatHeaderDuration(Math.max(0, Date.now() - startTimestamp)));
        };
        tick();
        const timerId = window.setInterval(tick, HEADER_DURATION_TICK_MS);
        return () => window.clearInterval(timerId);
    }, [startTimestamp]);
    useEffect(() => {
        if (!localParticipantId) {
            setHasConnectionStats(false);
            return;
        }
        const onStats = (stats = {}) => {
            const quality = stats.connectionQuality ?? stats.percent;
            if (typeof quality === 'number') {
                setConnectionQuality(quality);
                setHasConnectionStats(true);
            }
        };
        statsEmitter.subscribeToClientStats(localParticipantId, onStats);
        setHasConnectionStats(true);
        return () => {
            statsEmitter.unsubscribeToClientStats(localParticipantId, onStats);
        };
    }, [localParticipantId]);
    const signal = resolveSignalDisplay(connectionQuality, hasConnectionStats);
    const onShareMeeting = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.shareMeeting'));
        logger.info(`[会议顶栏] ${new Date().toISOString()} 打开分享会议`);
        dispatch(beginAddPeople());
    }, [dispatch]);
    const onToggleAiNotes = useCallback(() => {
        sendAnalytics(createToolbarEvent('header.ainotes'));
        logger.info(`[会议顶栏] ${new Date().toISOString()} 切换 AI 纪要 | open=${!aiNotesOpen}`);
        dispatch(toggleSidePanelApp(SidePanelTabId.AI_NOTES));
    }, [aiNotesOpen, dispatch]);
    return (React.createElement("div", { className: classes.root },
        React.createElement("span", { "aria-label": t('conferenceHeader.meetingDurationLabel'), className: classes.timer }, durationText),
        React.createElement("span", { "aria-label": t('conferenceHeader.connectionQuality', {
                percent: hasConnectionStats ? connectionQuality : 0
            }), className: classes.signal, title: t('conferenceHeader.connectionQuality', {
                percent: hasConnectionStats ? connectionQuality : 0
            }) }, Array.from({ length: SIGNAL_BAR_COUNT }).map((_, index) => (React.createElement("span", { className: classes.signalBar, key: `signal-${index}`, style: {
                background: index < signal.level ? signal.color : SIGNAL_COLOR_IDLE,
                height: `${4 + (index * 3)}px`,
                opacity: index < signal.level ? 1 : 0.45
            } })))),
        React.createElement("button", { "aria-label": t('conferenceHeader.shareMeeting'), className: classes.iconButton, onClick: onShareMeeting, title: t('conferenceHeader.shareMeeting'), type: 'button' },
            React.createElement(Icon, { size: HEADER_LEFT_ICON_SIZE_PX, src: IconShare })),
        aiNotesEnabled && (React.createElement("button", { "aria-label": t('toolbar.ainotes'), "aria-pressed": aiNotesOpen, className: cx(classes.iconButton, aiNotesOpen && classes.iconButtonActive), onClick: onToggleAiNotes, title: t('toolbar.ainotes'), type: 'button' },
            React.createElement(Icon, { size: HEADER_LEFT_ICON_SIZE_PX, src: IconShareDoc }))),
        React.createElement("span", { "aria-hidden": 'true', className: classes.divider })));
};
export default ConferenceHeaderLeft;
